import {
  Radio,
  Settings,
  LogOut,
  Menu,
  X,
  Building2,
  LayoutGrid,
  UsersRound,
  Cpu,
  BrainCircuit,
  ClipboardList,
  Shield,
  ScrollText,
  KeyRound,
  Wifi,
  Download,
  Headphones,
  Activity,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuthStore } from "../../features/auth/store/authStore";
import { useUiStore } from "../../store/uiStore";
import { usePropertyStore, useSelectedPropertyType } from "../../store/propertyStore";
import { NAV_BY_PROPERTY_TYPE, type NavItem } from "../../constants/navigation";

const MANAGE_NAV: NavItem[] = [
  { label: "Admin Settings", path: "/admin", Icon: Settings },
];

const TENANT_SUPPORT_NAV: NavItem[] = [
  { label: "Support Tickets", path: "/support-tickets", Icon: Headphones },
];

// Platform admin navigation
const PLATFORM_OVERVIEW_NAV: NavItem[] = [
  { label: "Live Ops", path: "/platform-admin/live-ops", Icon: Radio },
  { label: "Monitoring", path: "/platform-admin/monitoring", Icon: Activity },
];

const PLATFORM_MANAGEMENT_NAV: NavItem[] = [
  { label: "Organisations", path: "/platform-admin/organizations", Icon: Building2 },
  { label: "Properties", path: "/platform-admin/properties", Icon: LayoutGrid },
  { label: "Teams", path: "/platform-admin/teams", Icon: UsersRound },
  { label: "Users & Role", path: "/platform-admin/users", Icon: UsersRound },
  { label: "Devices & Hardware", path: "/platform-admin/devices", Icon: Cpu },
  { label: "Device Health", path: "/platform-admin/device-health", Icon: Wifi },
  { label: "OTA Management", path: "/platform-admin/ota", Icon: Download },
];

const PLATFORM_INTELLIGENCE_NAV: NavItem[] = [
  { label: "AI & Processing", path: "/platform-admin/ai-settings", Icon: BrainCircuit },
  { label: "SLA & Escalation", path: "/platform-admin/sla-rules", Icon: ClipboardList },
];

const PLATFORM_ADMIN_NAV: NavItem[] = [
  { label: "Platform Admins", path: "/platform-admin/admins", Icon: Shield },
  { label: "Audit Logs", path: "/platform-admin/audit-logs", Icon: ScrollText },
  { label: "Password Reset", path: "/platform-admin/password-reset", Icon: KeyRound },
  { label: "Support Tickets", path: "/platform-admin/support", Icon: Headphones },
];

function NavGroup({
  label,
  items,
  collapsed,
}: {
  label: string;
  items: NavItem[];
  collapsed: boolean;
}): JSX.Element {
  return (
    <div className="mb-4">
      {!collapsed && (
        <p className="mb-1 px-4 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
          {label}
        </p>
      )}
      {items.map(({ label: itemLabel, path, Icon }) => (
        <NavLink
          key={path}
          to={path}
          title={itemLabel}
          className={({ isActive }) =>
            `flex items-center ${
              collapsed ? "justify-center gap-0" : "gap-3"
            } rounded-lg px-4 py-2.5 text-sm transition-colors ${
              isActive
                ? "bg-brand-green font-semibold text-black"
                : "text-black hover:bg-black/10"
            }`
          }
        >
          <Icon size={16} />
          {!collapsed && itemLabel}
        </NavLink>
      ))}
    </div>
  );
}

export function Sidebar(): JSX.Element {
  const openLogoutModal = useAuthStore((s) => s.openLogoutModal);
  const user = useAuthStore((s) => s.user);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const mobileOpen = useUiStore((s) => s.mobileSidebarOpen);
  const closeMobileSidebar = useUiStore((s) => s.closeMobileSidebar);
  const location = useLocation();
  const propertyType = useSelectedPropertyType();
  const hasNoProperties = usePropertyStore((s) => s.initialized && s.properties.length === 0);
  const organizationName = user?.organization_name || "QuickComm";
  // Which pages exist depends on the property, so until one is picked there is
  // no honest Operations group to show.
  const operationsNav = propertyType ? NAV_BY_PROPERTY_TYPE[propertyType] : null;

  useEffect(() => {
    try {
      const stored = localStorage.getItem("qc.sidebar.collapsed");
      if (stored !== null) setCollapsed(stored === "true");
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("qc.sidebar.collapsed", String(collapsed));
    } catch { /* ignore */ }
  }, [collapsed]);

  // Close the mobile drawer when navigating or when the viewport grows to desktop
  useEffect(() => {
    closeMobileSidebar();
  }, [location.pathname, closeMobileSidebar]);

  useEffect(() => {
    if (!mobileOpen) return;
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) closeMobileSidebar();
    };
    mq.addEventListener("change", onChange);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMobileSidebar();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      mq.removeEventListener("change", onChange);
      window.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen, closeMobileSidebar]);

  // The drawer is always fully expanded on mobile; collapse only applies on desktop
  const effectiveCollapsed = collapsed && !mobileOpen;

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          aria-hidden="true"
          onClick={closeMobileSidebar}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-[#E5E7EB] bg-sidebar py-4 transition-transform duration-200 md:static md:z-auto md:flex-shrink-0 md:translate-x-0 md:transition-all ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } ${collapsed ? "md:w-16" : "md:w-60"}`}
      >
      <div className="mb-6 flex items-center justify-between px-2">
        <div className="flex min-w-0 items-center gap-2 px-2">
          <button
            aria-label={collapsed ? "Open sidebar" : "Collapse sidebar"}
            onClick={() => setCollapsed((c) => !c)}
            className="hidden rounded p-2 text-gray-300 hover:bg-white/5 md:block"
          >
            <Menu size={18} />
          </button>
          {!effectiveCollapsed && (
            <div className="overflow-hidden px-1">
              <span className="truncate text-xs font-semibold text-gray-600">
                {organizationName}
              </span>
            </div>
          )}
        </div>
        <button
          aria-label="Close navigation menu"
          onClick={closeMobileSidebar}
          className="flex h-10 w-10 items-center justify-center rounded text-gray-500 hover:bg-black/10 md:hidden"
        >
          <X size={18} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2">
        {user?.is_platform_admin ? (
          <>
            <NavGroup label="Overview" items={PLATFORM_OVERVIEW_NAV} collapsed={effectiveCollapsed} />
            <NavGroup label="Management" items={PLATFORM_MANAGEMENT_NAV} collapsed={effectiveCollapsed} />
            <NavGroup label="Intelligence" items={PLATFORM_INTELLIGENCE_NAV} collapsed={effectiveCollapsed} />
            <NavGroup label="Platform" items={PLATFORM_ADMIN_NAV} collapsed={effectiveCollapsed} />
          </>
        ) : (
          <>
            {operationsNav ? (
              <NavGroup label="Operations" items={operationsNav} collapsed={effectiveCollapsed} />
            ) : (
              !effectiveCollapsed && (
                <p className="mb-4 px-4 text-[12px] text-gray-400">
                  Select a property to see its pages.
                </p>
              )
            )}
            {/* Admin Settings is scoped to the selected property too — its
                tabs differ by vertical — so it waits for one like the rest. */}
            {user?.role === 'SUPER_ADMIN' && (propertyType || hasNoProperties) && (
              <NavGroup label="Manager" items={MANAGE_NAV} collapsed={effectiveCollapsed} />
            )}
            <NavGroup label="Help" items={TENANT_SUPPORT_NAV} collapsed={effectiveCollapsed} />
          </>
        )}
      </nav>

      <div className="px-2">
        <button
          onClick={openLogoutModal}
          title="Logout"
          className={`mt-2 flex rounded-lg bg-brand-green text-black transition-opacity hover:opacity-70 ${
            effectiveCollapsed
              ? "w-full items-center justify-center px-4 py-2.5"
              : "w-full items-center gap-2 px-4 py-2.5 text-sm "
          }`}
        >
          <LogOut size={16} />
          {!effectiveCollapsed && "Logout"}
        </button>
      </div>
      </aside>
    </>
  );
}
